// Assign to Products — sport-artwork template helpers.
// Designs bind here (≤2 + keep-logo). Colors and the blank list live on
// Product lineups; this page copies a lineup style, it does not invent
// print positions.

export const TARGET_GARMENTS = 7;
export const MAX_DESIGNS = 2;

export type DesignLike = {
  designTemplateId?: string;
  position?: string;
};

export type StyleLike = {
  styleNumber?: string;
  colors?: Record<string, boolean>;
  designs?: DesignLike[];
  keepLogoProduct?: boolean;
};

export type Coverage = {
  garments: number;
  withDesigns: number;
  missingDesigns: number;
  overflow: number;
  samePosition: number;
};

export function defaultPrintPosition(locations: { position?: string }[] | undefined): string {
  return String(locations?.[0]?.position || '').trim();
}

export function samePositionConflict(designs: DesignLike[] | undefined): boolean {
  const pos = (designs || []).map((d) => String(d.position || '').trim()).filter(Boolean);
  return pos.length >= 2 && new Set(pos).size < pos.length;
}

function hasBoundDesign(s: StyleLike): boolean {
  return (s.designs || []).some((d) => !!d.designTemplateId && !!String(d.position || '').trim());
}

export function sportCoverage(
  styles: StyleLike[] | undefined,
  opts: { logoOnly?: boolean; maxDesigns?: number } = {},
): Coverage {
  const list = styles || [];
  const maxDesigns = opts.maxDesigns ?? MAX_DESIGNS;
  const missing = opts.logoOnly ? 0 : list.filter((s) => !hasBoundDesign(s)).length;
  return {
    garments: list.length,
    withDesigns: opts.logoOnly ? list.length : list.length - missing,
    missingDesigns: missing,
    overflow: list.filter((s) => (s.designs || []).length > maxDesigns).length,
    samePosition: list.filter((s) => samePositionConflict(s.designs)).length,
  };
}

export function coverageLabel(c: Coverage, logoOnly = false): string {
  if (logoOnly) return `${c.garments} logo-only`;
  if (!c.garments) return 'empty';
  if (c.missingDesigns === 0 && c.samePosition === 0) return `${c.garments} ready`;
  const bits = [`${c.withDesigns}/${c.garments}`];
  if (c.missingDesigns) bits.push(`${c.missingDesigns} need design`);
  if (c.samePosition) bits.push(`${c.samePosition} same spot`);
  return bits.join(' · ');
}

export function lineupStylesMissingFromSport<T extends { styleNumber: string }>(
  lineup: T[] | undefined,
  sport: { styleNumber?: string }[] | undefined,
): T[] {
  const have = new Set((sport || []).map((s) => String(s.styleNumber || '').toUpperCase()).filter(Boolean));
  return (lineup || []).filter((s) => !have.has(String(s.styleNumber || '').toUpperCase()));
}

export function keepsSchoolLogo(style: Pick<StyleLike, 'keepLogoProduct'> | undefined): boolean {
  return !style || style.keepLogoProduct !== false;
}

export function styleFromLineup(
  lineupStyle: { styleNumber: string; colors?: Record<string, boolean> },
  keepLogo: boolean,
): StyleLike & { styleNumber: string } {
  const colors = Object.fromEntries(Object.entries(lineupStyle.colors || {}).filter(([, v]) => v));
  return {
    styleNumber: lineupStyle.styleNumber,
    colors,
    designs: [],
    keepLogoProduct: keepLogo,
  };
}

export function colorSummary(colors: Record<string, boolean> | undefined): string {
  return Object.entries(colors || {}).filter(([, v]) => v).map(([k]) => k).join(', ');
}
